import { useState, useEffect, useRef } from 'react';

/**
 * Screenshot protection hook.
 *
 * Detects PrintScreen key presses and Mac screenshot shortcuts
 * (Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5) and briefly shows a
 * protection overlay. Does NOT trigger on regular tab switches or
 * window blur — only on explicit screenshot key combos.
 *
 * Returns { isProtected } — true for ~2 seconds after a detected attempt.
 */
export function useScreenshotProtection(enabled: boolean) {
  const [isProtected, setIsProtected] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const trigger = () => {
      setIsProtected(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setIsProtected(false);
        timerRef.current = null;
      }, 2000);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Windows/Linux: PrintScreen key
      if (e.key === 'PrintScreen') {
        trigger();
        return;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Mac: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5 (screenshot shortcuts)
      if (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5')) {
        trigger();
        return;
      }
      // Windows: Ctrl+PrintScreen
      if (e.ctrlKey && e.key === 'PrintScreen') {
        trigger();
        return;
      }
      // Windows Snipping Tool: Win+Shift+S doesn't fire reliably, but capture what we can
      // Alt+PrintScreen (window screenshot on Windows)
      if (e.altKey && e.key === 'PrintScreen') {
        trigger();
        return;
      }
    };

    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('keydown', handleKeyDown);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled]);

  return { isProtected };
}
